import * as React from "react";
import { cn } from "./ui/index.ts";
import { I } from "./icons/icons.tsx";
import { usePromptDialog } from "../core/providers/prompt-dialog.provider.tsx";

// ── Toolbar button ────────────────────────────────────────────────────────────
function Btn({
  children,
  cmd,
  arg,
  title,
  onAction,
}: {
  children: React.ReactNode;
  cmd?: string;
  arg?: string;
  title: string;
  onAction?: () => void;
}) {
  return (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => {
        e.preventDefault(); // keep editor focus
        if (onAction) onAction();
        else if (cmd) document.execCommand(cmd, false, arg ?? "");
      }}
      className="inline-flex h-7 min-w-7 items-center justify-center rounded px-1 text-[13px] font-semibold text-sf-text hover:bg-sf-primary/10 hover:text-sf-primary active:bg-sf-primary/20"
    >
      {children}
    </button>
  );
}

function Sep() {
  return <span className="mx-0.5 h-4 w-px self-center bg-sf-border" />;
}

async function insertLinkWithDialog(
  dialog: ReturnType<typeof usePromptDialog>,
): Promise<void> {
  // On sauvegarde la sélection courante avant d'ouvrir la modale (le focus
  // quitte l'éditeur), pour pouvoir la restaurer avant `createLink`.
  const selection = window.getSelection();
  const range =
    selection && selection.rangeCount > 0
      ? selection.getRangeAt(0).cloneRange()
      : null;
  const url = await dialog.prompt({
    title: "Insérer un lien",
    message: "Saisissez l'URL à associer à la sélection.",
    placeholder: "https://exemple.fr",
    defaultValue: "https://",
    confirmLabel: "Insérer",
  });
  if (!url) return;
  if (range && selection) {
    selection.removeAllRanges();
    selection.addRange(range);
  }
  document.execCommand("createLink", false, url);
}

/** Strip HTML tags for plain-text previews */
export function stripHtml(html: string): string {
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent ?? "").replace(/\s+/g, " ").trim();
}

// ── Editor ────────────────────────────────────────────────────────────────────
interface RichTextEditorProps {
  /** Initial HTML content */
  defaultHtml?: string;
  placeholder?: string;
  className?: string;
  /** Called with current innerHTML when user clicks Save */
  onSave: (html: string) => void;
  onCancel: () => void;
}

export function RichTextEditor({
  defaultHtml = "",
  placeholder,
  className,
  onSave,
  onCancel,
}: RichTextEditorProps) {
  const editorRef = React.useRef<HTMLDivElement>(null);
  const dialog = usePromptDialog();

  // Seed the editor once
  React.useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = defaultHtml;
      editorRef.current.focus();
    }
  }, []); // intentionally empty: seed editor DOM once on mount

  const handleSave = () => {
    onSave(editorRef.current?.innerHTML ?? "");
  };

  return (
    <div
      className={cn(
        "flex flex-col overflow-hidden rounded-lg border border-sf-primary/40 shadow-sm",
        className,
      )}
    >
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 border-b border-sf-border bg-sf-surface-alt px-1.5 py-1">
        {/* History */}
        <Btn cmd="undo" title="Annuler (Ctrl+Z)">
          <I.Undo size={13} />
        </Btn>
        <Btn cmd="redo" title="Rétablir (Ctrl+Y)">
          <I.Redo size={13} />
        </Btn>
        <Sep />

        {/* Text style */}
        <Btn cmd="bold" title="Gras (Ctrl+B)">
          <strong>B</strong>
        </Btn>
        <Btn cmd="italic" title="Italique (Ctrl+I)">
          <em>I</em>
        </Btn>
        <Btn cmd="underline" title="Souligné (Ctrl+U)">
          <u>U</u>
        </Btn>
        <Btn cmd="strikeThrough" title="Barré">
          <s>S</s>
        </Btn>
        <Btn cmd="subscript" title="Indice">
          <span className="text-[10px]">
            x<sub>2</sub>
          </span>
        </Btn>
        <Btn cmd="superscript" title="Exposant">
          <span className="text-[10px]">
            x<sup>2</sup>
          </span>
        </Btn>
        <Btn cmd="removeFormat" title="Effacer la mise en forme">
          <span className="text-[11px]">T̸x</span>
        </Btn>
        <Sep />

        {/* Headings */}
        <Btn cmd="formatBlock" arg="h2" title="Titre 1">
          <span className="text-[11px] font-bold">H1</span>
        </Btn>
        <Btn cmd="formatBlock" arg="h3" title="Titre 2">
          <span className="text-[10px] font-bold">H2</span>
        </Btn>
        <Btn cmd="formatBlock" arg="p" title="Paragraphe">
          <span className="text-[12px]">¶</span>
        </Btn>
        <Btn cmd="formatBlock" arg="blockquote" title="Citation">
          <I.Quote size={13} />
        </Btn>
        <Sep />

        {/* Alignment */}
        <Btn cmd="justifyLeft" title="Aligner à gauche">
          <I.AlignLeft size={13} />
        </Btn>
        <Btn cmd="justifyCenter" title="Centrer">
          <I.AlignCenter size={13} />
        </Btn>
        <Btn cmd="justifyRight" title="Aligner à droite">
          <I.AlignRight size={13} />
        </Btn>
        <Sep />

        {/* Lists */}
        <Btn cmd="insertUnorderedList" title="Liste à puces">
          <I.List size={13} />
        </Btn>
        <Btn cmd="insertOrderedList" title="Liste numérotée">
          <I.ListChecks size={13} />
        </Btn>
        <Sep />

        {/* Indent */}
        <Btn cmd="indent" title="Indenter">
          <I.ChevronRight size={13} />
        </Btn>
        <Btn cmd="outdent" title="Désindenter">
          <I.ChevronLeft size={13} />
        </Btn>
        <Sep />

        {/* Links & separators */}
        <Btn
          onAction={() => {
            void insertLinkWithDialog(dialog);
          }}
          title="Insérer un lien"
        >
          <I.Link size={13} />
        </Btn>
        <Btn cmd="unlink" title="Supprimer le lien">
          <I.Unlink size={13} />
        </Btn>
        <Btn cmd="insertHorizontalRule" title="Séparateur">
          <span className="text-[12px]">—</span>
        </Btn>

        <div className="flex-1" />

        {/* Save / Cancel */}
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex h-7 items-center gap-1 rounded border border-sf-border bg-white px-2 text-[11.5px] font-semibold text-sf-text hover:bg-gray-50"
        >
          <I.X size={11} /> Annuler
        </button>
        <button
          type="button"
          onClick={handleSave}
          className="inline-flex h-7 items-center gap-1 rounded bg-sf-primary px-2 text-[11.5px] font-semibold text-white hover:opacity-90"
        >
          <I.Check size={11} /> Enregistrer
        </button>
      </div>

      {/* Editable area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder ?? "Saisir le contenu…"}
        className="prose-editor min-h-32 flex-1 overflow-y-auto bg-white px-3.5 py-3 text-[13px] leading-relaxed text-sf-text outline-none"
      />
    </div>
  );
}

// ── Read-only rich text display ───────────────────────────────────────────────
export function RichTextDisplay({
  html,
  className,
}: {
  html: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "prose-editor text-[13px] leading-relaxed text-sf-text",
        className,
      )}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: trusted internal HTML from the editor
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
